import { Flex } from '@mantine/core';
import { UserHeader, UserSintiaChatReadonly } from '@widgets/user';
import { UserInfoRecruiter } from './UserInfoRecruiter';
import { UserInfoManager } from './UserInfoManager';
import { UserInfoSupport } from './UserInfoSupport';
import { UserInfoAdmin } from './UserInfoAdmin';
import { VacancyList } from '@widgets/vacancy';
import { useChangeActivity, type User } from '@entities/user';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '@shared/config';
import { ROLES } from '@shared/constants';
import { showNotification } from '@shared/utils';

const SINTIA_CHAT_ROLES = new Set([ROLES.MANAGER, ROLES.RECRUITER]);

const USER_CARDS: Record<
  string,
  React.ComponentType<{ user: User; editLink: string }>
> = {
  recruiter: UserInfoRecruiter,
  manager: UserInfoManager,
  administrator: UserInfoAdmin,
  support: UserInfoSupport,
};

type UserInfoProps = {
  user: User;
  viewAllHref: string;
};

export const UserInfo = ({ user, viewAllHref }: UserInfoProps) => {
  const queryClient = useQueryClient();
  const roleName = user.role?.name;
  const UserCard = roleName ? USER_CARDS[roleName] : undefined;

  const { mutate: changeActivity, isPending: isPendingChangeActivity } =
    useChangeActivity(user.id.toString());

  const handleChangeActivity = () => {
    changeActivity(
      { isActive: !user.isActive },
      {
        onSuccess: data => {
          queryClient.setQueryData(
            [QueryKey.GET_USER, user.id.toString()],
            (prev: User) => ({
              ...prev,
              ...data,
            })
          );
          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_USERS_WITH_PAGINATION],
          });
        },
        onError: error => {
          showNotification({
            error,
            variant: 'error',
            message:
              error.response?.data.message ||
              'Не удалось изменить активность пользователя',
          });
        },
      }
    );
  };

  return (
    <Flex
      wrap='wrap'
      direction='column'
      gap={32}
      pt={16}
      mx='auto'
      maw={1512}
      w='100%'
    >
      <UserHeader
        isDelete={false}
        createdAt={user.createdAt}
        id={user.id}
        firstName={user.profile.firstName}
        lastName={user.profile.lastName}
        group={user.role?.displayName}
        company={user?.company?.name}
        isShowActivity
        isActive={user.isActive}
        handleChangeActivity={handleChangeActivity}
        isPendingChangeActivity={isPendingChangeActivity}
      />

      <Flex
        wrap='wrap'
        gap={24}
        align='flex-start'
        direction={{ base: 'column', md: 'row' }}
      >
        <Flex direction='column' gap={24} flex={1} w='100%' maw={1070}>
          {UserCard && (
            <UserCard user={user} editLink={`/user-list/${user.id}/edit`} />
          )}
          {roleName && SINTIA_CHAT_ROLES.has(roleName) && (
            <UserSintiaChatReadonly user={user} />
          )}
        </Flex>

        <Flex direction='column' gap={24}>
          <VacancyList
            title='Вакансии'
            vacancies={user.vacancies.slice(0, 3)}
            totalVacancies={user.vacancies.length}
            viewAllHref={viewAllHref}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
